import { clsx } from 'clsx'
import type { ReactNode } from 'react'
import { Eyebrow } from './Eyebrow'

export type HeroMedia = { url: string; type: 'image' | 'video'; overlay?: number }

/**
 * Dark full-bleed section used for the welcome and team screens. With `media`, the image or muted
 * looping video sits behind the copy under a dark gradient (`overlay` 0–1, default 0.6) so text stays legible.
 */
export function Hero({ eyebrow, title, children, actions, media, className }: { eyebrow?: string; title: ReactNode; children?: ReactNode; actions?: ReactNode; media?: HeroMedia | null; className?: string }) {
  const overlay = Math.min(1, Math.max(0, media?.overlay ?? 0.6))
  return (
    <section className={clsx('on-dark relative overflow-hidden bg-eerie text-paper', className)}>
      {media && (
        <div aria-hidden="true" className="absolute inset-0">
          {media.type === 'video' ? (
            <video src={media.url} autoPlay muted loop playsInline preload="auto" className="h-full w-full object-cover" />
          ) : (
            <img src={media.url} alt="" decoding="async" fetchPriority="high" className="h-full w-full object-cover" />
          )}
          {/* Even darkening plus a heavier left edge, where the copy sits. */}
          <div className="absolute inset-0 bg-eerie" style={{ opacity: overlay }} />
          <div className="absolute inset-0 bg-gradient-to-r from-eerie/80 via-eerie/30 to-transparent" />
        </div>
      )}
      <div className="relative mx-auto grid max-w-portal gap-8 px-6 py-section-sm md:px-10 md:py-section">
        {eyebrow && <Eyebrow tone="inverse">{eyebrow}</Eyebrow>}
        <h1 className="max-w-4xl text-paper">{title}</h1>
        {children && <div className="max-w-2xl text-[1.0625rem] leading-relaxed text-paper/80">{children}</div>}
        {actions && <div className="flex flex-wrap gap-3 pt-2">{actions}</div>}
      </div>
    </section>
  )
}
